/*
 * @Author: ken yang
 * @Date: 2023-04-23 11:43:04
 * @LastEditTime: 2023-08-25 17:44:38
 * @LastEditors: ken yang
 * @Description:
 * @FilePath: /uexch_pc/src/store/quotes.js
 */
import { offSpotTicker, onSpotTicker } from '@/utils/ws/spotTickerTopic.js'
import { createQuotesTickerBatch } from '@/store/quotesTickerBatch.js'

const SPOT_TICKER_BATCH_INTERVAL = 200

const subSocketTicker = (cb) => {
  const quotesEventName = WS_KEYS.ticker + '@quotes'
  const ws = Socket.instance
  if (ws?.hasEvent(quotesEventName)) {
    return
  }

  onSpotTicker(quotesEventName, (data) => {
    cb(data)
  })
}

const unsubSocketTicker = () => {
  const quotesEventName = WS_KEYS.ticker + '@quotes'

  offSpotTicker(quotesEventName)
}

const subContractSocketTicker = (cb) => {
  const ws = FuturesSocket.autoConnect()

  const wsEventName = WS_CONTRACT_KEYS.ticker
  const quotesEventName = WS_CONTRACT_KEYS.ticker + '@quotes'
  if (ws.hasEvent(quotesEventName)) {
    return
  }

  const sendData = {
    'id': randomString(),
    'cmd': 'sub',
    'topic': wsEventName,
    'data': {}
  }

  ws.on(quotesEventName, (data) => {
    cb(data)
  })
  ws.send(sendData)
}

const updateTickersCb = {}
const updateContractTickersCb = {}
let getTickersPromise = null
let spotTickerBatch = null

const useQuotesStore = defineStore('quotesStore', {
  state: () => {
    return {
      tickers: [], // 币币行情
      symbolsAll: [], // 合约交易对
      coins: [], // 现货币种
      optionals: [], // 现货的自选
      contractTickers: [], // 合约行情
      contractSymbolsAll: [], // 交易对
      contractCoins: [], // 合约币种
      contractOptionals: [], // 合约的自选
      rankingsConfig: [], // 榜单配置
      usdtUsdRate: null, // USDT/USD 汇率缓存
      usdtUsdRateRequested: false, // 当前会话内只请求一次
    }
  },
  getters: {
    coinImageMap: (state) => {
      return state.coins.reduce((o, e) => ({ ...o, [e.coinName]: e.iconUrl }), {})
    },
    // 将数组转为以 coinName 为 key 的 Hash Map，在数据不变更时会被持久缓存，转换时间极小
    coinMap: (state) => {
      return state.coins.reduce((map, coin) => {
        map[coin.coinName] = coin
        return map
      }, {})
    },
    // 提供一个灵活获取币种任意属性的方法，时间复杂度 O(1)，性能极佳
    getCoinProp () {
      return (coinName, propKey, fallback = '') => {
        const coin = this.coinMap[coinName]
        return coin && coin[propKey] !== undefined && coin[propKey] !== null ? coin[propKey] : fallback
      }
    },
    symbolsAllMap: (state) => {
      return state.symbolsAll.reduce((o, e) => ({ ...o, [e.coinMarket]: e }), {})
    },
    tickersMap: (state) => {
      return state.tickers.reduce((map, ticker) => {
        map[ticker.coinMarket] = ticker
        return map
      }, {})
    },
    tickerByCoinIdMap: (state) => {
      return state.tickers.reduce((map, ticker) => {
        if (ticker.coinId === null || ticker.coinId === undefined || ticker.coinId === '') return map

        const key = String(ticker.coinId)
        const quoteCoinName = ticker.coinMarket?.split('/')[1]
        if (!map[key] || quoteCoinName === 'USDT') {
          map[key] = ticker
        }
        return map
      }, {})
    },
    contractSymbolsAllMap: (state) => {
      return state.contractSymbolsAll.reduce((o, e) => ({ ...o, [e.coinMarket]: e }), {})
    },
    contractTickersMap: (state) => {
      return state.contractTickers.reduce((map, ticker) => {
        map[ticker.coinMarket] = ticker
        return map
      }, {})
    },
    /**
     * 获取单个现货币对行情
     * @param {string} coinMarket 币对标识，如 `BTC/USDT`
     * @param {*} [fallback=null] 未命中时返回的兜底值
     * @returns {*} 返回对应币对的现货 ticker，未命中时返回 fallback
     */
    getSpotTicker () {
      return (coinMarket, fallback = null) => {
        return this.tickersMap[coinMarket] ?? fallback
      }
    },
    getUsdtPrice () {
      return (coinName) => {
        if (!coinName) return null
        if (coinName === 'USDT') return BigNumber(1)

        const directPrice = BigNumber(this.getSpotTicker(`${coinName}/USDT`)?.lastPrice)
        if (directPrice.isFinite() && directPrice.gt(0)) return directPrice

        const reversePrice = BigNumber(this.getSpotTicker(`USDT/${coinName}`)?.lastPrice)
        return reversePrice.isFinite() && reversePrice.gt(0) ? BigNumber(1).div(reversePrice) : null
      }
    },
    getUsdtPriceByCoinId () {
      return (coinId) => {
        if (coinId === null || coinId === undefined || coinId === '') return null

        const ticker = this.tickerByCoinIdMap[String(coinId)]
        if (!ticker?.coinMarket) return null

        const [baseCoinName, quoteCoinName] = ticker.coinMarket.split('/')
        if (baseCoinName === 'USDT') return BigNumber(1)

        const basePrice = BigNumber(ticker.lastPrice)
        if (!basePrice.isFinite() || basePrice.lte(0)) return null
        if (quoteCoinName === 'USDT') return basePrice

        const quoteUsdtPrice = this.getUsdtPrice(quoteCoinName)
        return quoteUsdtPrice?.isFinite() && quoteUsdtPrice.gt(0)
          ? basePrice.times(quoteUsdtPrice)
          : null
      }
    },
    /**
     * 获取单个合约币对行情
     * @param {string} coinMarket 币对标识，如 `BTCUSDT`
     * @param {*} [fallback=null] 未命中时返回的兜底值
     * @returns {*} 返回对应币对的合约 ticker，未命中时返回 fallback
     */
    getContractTicker () {
      return (coinMarket, fallback = null) => {
        return this.contractTickersMap[coinMarket] ?? fallback
      }
    },
    hasSymbolsAll: (state) => {
      return state.symbolsAll.length > 0
    },
    hasContractSymbolsAll: (state) => {
      return state.contractSymbolsAll.length > 0
    },
  },
  actions: {
    async getTickers () { // 现货行情接口调用
      if (getTickersPromise) {
        return getTickersPromise
      }

      getTickersPromise = (async () => {
        const params = { }
        const [tickers, tickerTops] = await Promise.all([
          quotesApi.tickers(params),
          quotesApi.tickersOnTop({}),
        ])
        const tickerTopCoinMarkets = tickerTops.map(e => e.coinMarket)

        for (let i = 0; i < tickers.length; i++) {
          const e = tickers[i]

          const cb = (data, newData) => {
            data.lastPrice = +newData.lastPrice
            data.rangeAbility = newData.rangeAbility
            data.highest = +newData.highest
            data.lowest = +newData.lowest
            data.amount = +newData.amount
            data.turnover = +newData.turnover
          }
          updateTickersCb[e.coinMarket] = cb
          updateTickersCb[e.coinMarket](tickers[i], tickers[i])
        }
        this.tickers = tickers.map(e => ({
          ...e,
          isHot: tickerTopCoinMarkets.includes(e.coinMarket)
        }))
        return this.tickers
      })()

      try {
        return await getTickersPromise
      } finally {
        getTickersPromise = null
      }
    },
    async getContractTickers () { // 合约行情接口调用
      const params = { coinMarketType: 'CONTRACT' }
      const contractTickers = await quotesApi.contractTickers(params)

      for (let i = 0; i < contractTickers.length; i++) {
        const e = contractTickers[i]
        const cb = (data, newData) => {
          data.lastPrice = +newData.lastPrice
          data.rangeAbility = newData.rangeAbility
          data.highest = +newData.highest
          data.lowest = +newData.lowest
          data.amount = +newData.amount
          data.turnover = +newData.turnover
        }
        updateContractTickersCb[e.coinMarket] = cb
        updateContractTickersCb[e.coinMarket](contractTickers[i], contractTickers[i])
      }
      this.contractTickers = contractTickers.map(e => ({
        ...e,
        lastPrice: +e.lastPrice,
        highest: +e.highest,
        lowest: +e.lowest,
        amount: +e.amount,
        turnover: +e.turnover,
        isHot: Boolean(e.isHot)
      }))
    },
    async getSymbolsAll  () { // 现货交易对接口调用
      const data = await quotesApi.coinMarkets({})
      this.$patch(state => {
        state.symbolsAll = data
      })
    },
    async getContractSymbolsAll  () { // 合约交易对接口调用
      const data = await quotesApi.contractCoinmarket({})
      this.$patch(state => {
        state.contractSymbolsAll = data
      })
    },
    async getCoins () {
      const data = await configApi.coinInfo({})
      this.$patch(state => {
        state.coins = data
      })
    },
    async getOptionals () {
      const data = await quotesApi.optionalList({})
      this.$patch(state => {
        state.optionals = data
      })
    },
    async getContractOptionalList () {
      const data = await quotesApi.contractOptionalList({})
      this.$patch(state => {
        state.contractOptionals = data
      })
    },
    async subPrice () {
      if (!spotTickerBatch) {
        spotTickerBatch = createQuotesTickerBatch({
          interval: SPOT_TICKER_BATCH_INTERVAL,
          onFlush: updates => {
            updates.forEach(data => {
              const ticker = this.tickersMap[data.coinMarket]
              const updateTicker = updateTickersCb[data.coinMarket]
              if (ticker && updateTicker) {
                updateTicker(ticker, data)
              }
            })
          },
        })
      }

      const cb = ({ data }) => {
        if (isEmpty(data)) {
          return
        }
        spotTickerBatch.schedule(data)
      }
      subSocketTicker(cb)
    },
    unsubPrice () {
      spotTickerBatch?.cancel()
      spotTickerBatch = null
      unsubSocketTicker()
    },
    async subContractPrice () {
      console.log('subContractPrice')
      const cb = ({ data }) => {
        if (isEmpty(data)) {
          return
        }
        const index = this.contractTickers.findIndex(e => e.coinMarket == data?.coinMarket)
        if (index != -1) {
          updateContractTickersCb[data?.coinMarket](this.contractTickers[index], data)
        }
      }
      subContractSocketTicker(cb)
    },
    runSocket () {
      this.subPrice()
      // this.subContractPrice()
    },
    async getRankingsConfig (data) {
      try {
        const res = await quotesApi.rankingsConfig({})
        this.rankingsConfig = res
      } catch (error) {
        console.log('quotesApi.rankingsConfig', error)
      }
    },
  },
  async init () {
    // this.getTickers()
    this.getSymbolsAll()
    this.getCoins()
    this.getContractSymbolsAll()
    // this.getContractTickers()
    if (localStorage.token) {
      // this.getOptionals()
      // this.getContractOptionalList()
    }
  },
})

export default useQuotesStore
